"use client";

import * as styles from "./styles.css";
import PageSection from "@/components/page-section";
import ContentContainer from "@/components/content-container";
import { useCallback, useEffect, useRef, useState } from "react";
import Card from "@/components/card";
import ButtonContainer from "@/components/button-container";
import Button from "@/components/button";
import { useSearchParams } from "next/navigation";

interface Props {
  sourceList: string;
  page?: string;
}

export default function SourceItemList(props: Props) {
  const [data, setData] = useState<any[]>([]);
  const [postCount, setPostCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const pageNumber = Number(searchParams.get("page")) || 0;

  const queryNews = async (
    count: number,
    endCount: number,
    sourceList: string
  ) => {
    try {
      const response = await fetch("/api/news", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          count,
          sourceList,
          endCount,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const { moreData } = await response.json();

      if (moreData) {
        setData([...data, ...moreData]);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during fetch:", error);
    }
  };

  const queryCount = async (sourceList: string) => {
    try {
      const response = await fetch("/api/news-count", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: sourceList,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { count } = await response.json();

      if (count) {
        setPostCount(count);
      }
    } catch (error) {
      console.error("Error during fetch:", error);
    }
  };

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const loadMore = async (event: any) => {
    event.preventDefault();
    const pageNumberIncrement = pageNumber === 0 ? 2 : 1;

    window.history.pushState(
      null,
      "",
      `?${createQueryString("page", (pageNumber + pageNumberIncrement).toString())}`
    );

    setLoading(true);
  };

  useEffect(() => {
    queryCount(props.sourceList);

    if (pageNumber > 1) {
      if (data.length <= 9) {
        setLoading(true);
        queryNews(
          data.length,
          data.length + 6 * (pageNumber - 1),
          props.sourceList
        );
      } else {
        queryNews(data.length, data.length + 6, props.sourceList);
      }
    } else {
      setLoading(true);
      queryNews(0, 9, props.sourceList);
    }
  }, [pageNumber]);

  return (
    <PageSection>
      <ContentContainer layout="sourceNews">
        <div
          ref={containerRef}
          className={`${styles.container} is--module is--articlemodule--article-tabs `}
        >
          <div className={styles.listContainer}>
            {data?.map((item: any) => (
              <div
                key={`item-${item._id}`}
                className={`${styles.contentContainer} is--active`}
              >
                <Card
                  title={item?.header?.headingText ?? item.title}
                  button={{
                    _type: "buttonObject",
                    buttonLabel: "Read more",
                    buttonVariant: "tertiary",
                    buttonVisible: true,
                    buttonLink: item.buttonLink,
                    linkType: "internal",
                  }}
                  bodyText={item.summary}
                  imageSource={item?.imagePreview?.url}
                  imageBlurData={item?.imagePreview?.lqip}
                  imageAlt={item?.imagePreview?.imageAlt}
                  variant="small"
                  type={"article"}
                  lineClamp={4}
                  date={item?.postDate}
                />
              </div>
            ))}
          </div>
          {postCount && postCount > data.length && (
            <div className={styles.loadmore}>
              <ButtonContainer spacing="mediumMobile">
                <Button
                  label={loading ? "Loading..." : "Load More"}
                  style="secondary"
                  size="large"
                  onClick={(event) => loadMore(event)}
                  disabled={loading}
                />
              </ButtonContainer>
            </div>
          )}
        </div>
      </ContentContainer>
    </PageSection>
  );
}
